import { useMutation, useQuery } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { useAtom } from "jotai";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import {
  fetchConsultationExport,
  startConsultationExport,
} from "#modules/quoteRequests/infra/exportConsultationZip.api";
import {
  consultationExportIdsAtom,
  withConsultationExportId,
} from "#modules/quoteRequests/view/exportConsultation/consultationExportIdsAtom";
import { ApiError } from "#shared/infra/apiError";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { ConsultationExportStatus, JobStatusSchema } from "shared";

const POLLING_INTERVAL_MS = 2000;

const triggerDownload = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const isExportRunning = (consultationExport: ConsultationExportStatus | undefined) =>
  consultationExport?.status !== JobStatusSchema.enum.PROCESSED &&
  consultationExport?.status !== JobStatusSchema.enum.ERROR;

const showExportError = (error: unknown, t: TFunction) => {
  errorHandler(error);
  showErrorToast({
    title: t("quoteRequestLine.actions.exportConsultationZip.unknownError"),
  });
};

export const useExportConsultationZip = ({
  quoteId,
  onDownloaded,
}: {
  quoteId: string;
  onDownloaded: () => void;
}) => {
  const { t } = useTranslation();
  const [exportIds, setExportIds] = useAtom(consultationExportIdsAtom);
  const exportId = exportIds[quoteId] ?? null;
  const onDownloadedRef = useRef(onDownloaded);
  onDownloadedRef.current = onDownloaded;

  const { mutate, isPending: isStarting } = useMutation({
    mutationFn: startConsultationExport,
    onSuccess: (response) =>
      setExportIds((previous) => withConsultationExportId(previous, quoteId, response.exportId)),
    onError: (startError) => showExportError(startError, t),
  });

  const { data: consultationExport, error } = useQuery({
    queryKey: queryKeys.quoteRequests.consultationExport(quoteId, exportId ?? ""),
    queryFn: () => fetchConsultationExport({ quoteId, exportId: exportId! }),
    enabled: exportId !== null,
    gcTime: 0,
    refetchInterval: (query) => (isExportRunning(query.state.data) ? POLLING_INTERVAL_MS : false),
  });

  const downloadUrl =
    consultationExport?.status === JobStatusSchema.enum.PROCESSED ? consultationExport.url : null;
  const isExpired = error instanceof ApiError && error.status === 404;
  const hasFailed = consultationExport?.status === JobStatusSchema.enum.ERROR || error !== null;

  useEffect(() => {
    if (!exportId) {
      return;
    }
    const clearExportId = () =>
      setExportIds((previous) => withConsultationExportId(previous, quoteId, null));
    if (downloadUrl) {
      clearExportId();
      triggerDownload(downloadUrl);
      showSuccessToast({ title: t("quoteRequestLine.actions.exportConsultationZip.success") });
      onDownloadedRef.current();
      return;
    }
    if (isExpired) {
      clearExportId();
      return;
    }
    if (hasFailed) {
      clearExportId();
      showExportError(error ?? new Error(`Consultation export ${exportId} failed`), t);
    }
  }, [exportId, quoteId, downloadUrl, isExpired, hasFailed, error, setExportIds, t]);

  return {
    exportConsultation: () => mutate(quoteId),
    isExportingConsultation: isStarting || exportId !== null,
  };
};
